"use client";

// WO-GF-001-P1B-S5 · Item 2 (M5) — PER-PRODUCT DATE PICKER. Document-scoped hydrator (the
// PopupLayer / ScheduleMapLayer / MarketplaceCartLayer family): it wires the CAPTURED per-product
// "Date Time" inputs (`.react-datepicker__input-container input`, dead photocopies — the legacy
// react-datepicker JS is stripped, R6-6) to a real calendar WITHOUT mutating their DOM at rest, so
// the pixel gate stays green (a listener + a data-behavior hook only; the calendar is a portal that
// renders nothing until a field is clicked).
//
// §0f (live snowbus.com, read-only): each product row's date field is a real react-datepicker — click
// opens a month calendar; the picked value fills the input, which the booking widget reads. On the
// build the input is already an editable text field prefilled with the crawl-day value, and the cart
// path (B3.4) already reads that value into the cart line's startDate/endDate at add-time. So the ONLY
// dead machinery here is the calendar UI. This layer restores it and writes the picked value back into
// the SAME input the cart already reads — no parallel path, zero change to the cart store (crown-jewel
// B3.4 flow untouched). The calendar open-state was never captured (click-gated), so per the sponsor-
// modal / schedule-map precedent it is a functional PH2-RESKIN shell, not a pixel-match of the legacy
// react-datepicker. minDate = today (a booking date cannot be in the past — a faithful booking default).
// Desktop only: mounted by MarketplaceSupplierDetail, which page.tsx renders on desktop only (F30).
//
// Harness hooks: data-behavior="date-picker" on each wired input; data-datepicker-popover on the calendar.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CalendarPopover, { type CalendarAnchor } from "@/components/site/CalendarPopover";

// Local-date format yyyy-mm-dd (never toISOString — that is UTC and shifts the day across the
// dateline). Matches the captured input value format exactly.
function fmt(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Parse a captured "yyyy-mm-dd" value into a LOCAL date (no timezone drift); null if empty/unparseable.
function parseLocal(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((v || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

type Anchor = CalendarAnchor;

/** Write a picked value back through React's own value tracker so controlled inputs register it. */
export function writeInputValue(target: HTMLInputElement, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
  if (setter) setter.call(target, value);
  else target.value = value;
  target.dispatchEvent(new Event("input", { bubbles: true }));
  target.dispatchEvent(new Event("change", { bubbles: true }));
}

export default function DatePickerLayer() {
  const [target, setTarget] = useState<HTMLInputElement | null>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  useEffect(() => {
    const inputs = Array.from(
      document.querySelectorAll<HTMLInputElement>(".react-datepicker__input-container input"),
    );
    const cleanups: Array<() => void> = [];
    for (const input of inputs) {
      input.setAttribute("data-behavior", "date-picker");
      input.style.cursor = "pointer";
      const open = (e: Event) => {
        // Keep the caret out of the mirror text field — the calendar is the interaction surface.
        e.preventDefault();
        const r = input.getBoundingClientRect();
        setAnchor({ top: r.bottom + window.scrollY, left: r.left + window.scrollX });
        setTarget(input);
      };
      input.addEventListener("mousedown", open);
      input.addEventListener("focus", open);
      cleanups.push(() => {
        input.removeEventListener("mousedown", open);
        input.removeEventListener("focus", open);
      });
    }
    return () => cleanups.forEach((c) => c());
  }, []);

  if (!target || !anchor) return null;
  const close = () => {
    setTarget(null);
    setAnchor(null);
  };
  return createPortal(
    <CalendarPopover
      anchor={anchor}
      selected={parseLocal(target.value)}
      ignoreEl={target}
      onPick={(d) => {
        // Write the picked value back into the SAME captured input the cart already reads (B3.4) —
        // no parallel path, cart store untouched.
        writeInputValue(target, fmt(d));
        close();
      }}
      onClose={close}
    />,
    document.body,
  );
}
